import { getAddress } from "viem";
import type { BootstrapResponse } from "../../../api/schemas";
import type { ReferralAuthorization } from "../api/referral-schemas";
import { responseSecondsRemaining } from "../../../api/response-time";
import { referralQualificationCurrent } from "../api/referral-qualification";
import { currentActionFacts } from "../../../api/action-capabilities";
import { decodeReferralMint, REFERRAL_EVIDENCE_MISSING, type ReferralMintEvidence } from "../contracts/referral-mint";
import type { WalletSessionController } from "../../../wallet/wallet-session";
import type { TransactionTracker } from "../../../wallet/transaction-tracker";
import type { ActionContext } from "../../../state/action-context";
import { isElysium, elysiumWriteBlock, elysiumMintStateBlock } from "../../../config/elysium";

export function referralSubmissionReason(bootstrap: BootstrapResponse | null, source: string | null,
  evidence: ReferralMintEvidence | null, environment: string): string | null {
  const v3Block = elysiumWriteBlock(bootstrap);
  if (v3Block) return v3Block;
  if (!evidence || !evidence.executionEnabled) return REFERRAL_EVIDENCE_MISSING;
  if (!bootstrap || !source) return "Referral deployment/source unavailable";
  if (isElysium(bootstrap)) {
    const readiness = elysiumMintStateBlock(bootstrap);
    if (readiness) return readiness;
  }
  const facts = currentActionFacts(bootstrap);
  if (!facts.writes || !facts.execution || !facts.deployment) return "Referral execution blocked by registered bootstrap write/execution gates";
  if (evidence.environment !== environment || evidence.chainId !== bootstrap.contracts.mintChainId ||
    evidence.collection.toLowerCase() !== bootstrap.contracts.collectionAddress?.toLowerCase() ||
    evidence.signer.toLowerCase() !== bootstrap.contracts.authorizationSignerAddress?.toLowerCase() ||
    evidence.sourceToken.toLowerCase() !== source.toLowerCase() || bootstrap.deployment.sourceTokenStatus !== "REGISTERED" ||
    !bootstrap.tokens.some(token => token.tracking && token.tokenAddress.toLowerCase() === source.toLowerCase())) {
    return "Referral evidence does not match registered deployment, signer, source or environment";
  }
  return null;
}

export async function submitReferralMint(result: ReferralAuthorization, guard: () => void, options: {
  bootstrap: BootstrapResponse | null; sourceToken: string | null; wallet: WalletSessionController;
  /** Current deployment/fee snapshot; null means authority is no longer available. */
  getBootstrap?: () => BootstrapResponse | null;
  evidence: ReferralMintEvidence | null; tracker: TransactionTracker; environment: string;
  beginOperation: ActionContext["beginOperation"]; endOperation: ActionContext["endOperation"];
}) {
  guard();
  const { wallet, tracker, sourceToken, evidence } = options;
  const readBootstrap = options.getBootstrap ?? (() => options.bootstrap);
  const bootstrap = readBootstrap();
  const reason = referralSubmissionReason(bootstrap, sourceToken, evidence, options.environment);
  if (reason || !bootstrap || !sourceToken || !evidence) throw new Error(reason ?? REFERRAL_EVIDENCE_MISSING);
  const account = wallet.getSnapshot().account;
  if (!account || !wallet.getSnapshot().authenticated) throw new Error("Referral mint requires the authenticated wallet");
  if (wallet.getSnapshot().chainId !== result.chainId) throw new Error(`Switch to chain ${result.chainId}, then prepare a fresh referral review.`);
  const decoded = decodeReferralMint(result, { wallet: account, sourceToken, chainId: bootstrap.contracts.mintChainId!,
    collection: bootstrap.contracts.collectionAddress!, milestone: result.milestone }, evidence);
  if (!decoded.identity) throw new Error(REFERRAL_EVIDENCE_MISSING);
  const provider = wallet.getProvider();
  if (!provider) throw new Error("Connect a wallet first");
  const scope = { account, chainId: result.chainId, collection: result.contractAddress, environment: options.environment };
  const operation = options.beginOperation();
  const assertCurrent = () => {
    operation.assertCurrent(); guard();
    if (referralSubmissionReason(readBootstrap(), sourceToken, evidence, options.environment)) throw new Error("Referral permission changed");
    if (tracker.referralClaimed(scope, result.milestone)) throw new Error("Referral milestone already claimed");
    if (tracker.getSnapshot().storageWarning || tracker.getSnapshot().referralClaimStorageWarning) throw new Error("Referral recovery storage unavailable");
    if (responseSecondsRemaining(result, result.deadline) <= 0) throw new Error("Referral authorization expired. Review again.");
    if (!referralQualificationCurrent(result)) throw new Error("Referral test qualification expired. Refresh and review again.");
  };
  try {
    assertCurrent();
    return await tracker.execute({ ...scope, kind: "MINT", sourceToken, tokenId: null, mintIdentity: decoded.identity }, provider, async () => {
      assertCurrent();
      return { account: getAddress(account), chainId: result.chainId, to: getAddress(result.call.to), data: result.call.data,
        valueWei: 0n, mintSigner: evidence.signer, referralMint: { identity: decoded.identity!, evidence, result },
        ...(isElysium(bootstrap) ? { mintBootstrap: readBootstrap } : {}) };
    }, { assertCurrent, signal: operation.signal });
  } finally { options.endOperation(operation); }
}
